/** L2 configurable-dashboard preferences: widget order + visibility, localStorage-persisted. */

export interface DashboardPrefs {
  order: string[];
  hidden: string[];
}

/** Widget ids in default order (mobile-first: most-actionable first). */
export const WIDGET_IDS = ['cockpit', 'alarms', 'hang', 'lab', 'production', 'ai'] as const;
export const WIDGET_LABELS: Record<string, string> = {
  cockpit: '总览指标',
  alarms: '活动告警',
  hang: '吊养水质',
  lab: '化验闭环',
  production: '生产与效率',
  ai: 'AI 建议与可信度',
};

export const DEFAULT_PREFS: DashboardPrefs = { order: [...WIDGET_IDS], hidden: [] };

const STORAGE_KEY = 'ras-dashboard-prefs';

/** Coerce arbitrary stored data into a valid prefs object (drop unknown, add missing). */
export function normalize(p: Partial<DashboardPrefs> | null | undefined): DashboardPrefs {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const id of p?.order ?? []) if (WIDGET_IDS.includes(id as (typeof WIDGET_IDS)[number]) && !seen.has(id)) { order.push(id); seen.add(id); }
  for (const id of WIDGET_IDS) if (!seen.has(id)) order.push(id); // any new widget appears at the end
  const hidden = (p?.hidden ?? []).filter((id) => seen.has(id));
  return { order, hidden };
}

export function toggleHidden(prefs: DashboardPrefs, id: string): DashboardPrefs {
  const hidden = prefs.hidden.includes(id) ? prefs.hidden.filter((h) => h !== id) : [...prefs.hidden, id];
  return { ...prefs, hidden };
}

/** Move a widget up (-1) or down (+1) within the order; no-op at the edges. */
export function move(prefs: DashboardPrefs, id: string, dir: -1 | 1): DashboardPrefs {
  const order = [...prefs.order];
  const i = order.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= order.length) return prefs;
  [order[i], order[j]] = [order[j], order[i]];
  return { ...prefs, order };
}

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

export function loadPrefs(storage: KV): DashboardPrefs {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? normalize(JSON.parse(raw)) : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: DashboardPrefs, storage: KV): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* storage full / unavailable — non-fatal */
  }
}
